// CS Import — import log: each run's counts, missing files, failures and
// unresolved links, so the team can review completeness before commonsku is
// retired (CEO spec 2026-10-16).
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { csDay } from "@/lib/csArchive";
import { CsNotReady, Crumbs } from "./parts";

interface Run { id: string; label: string; started_at: string; finished_at: string | null; status: string; summary: Record<string, unknown> & { clients?: Array<Record<string, number | string>> }; issue_counts: Record<string, number> }
interface Issue { id: string; kind: string; entity: string; cs_id: string | null; detail: string; created_at: string }
const KIND: Record<string, string> = { missing_file: "Missing file", failure: "Failed", unresolved: "Unresolved", note: "Note" };

export function CsImportLogPage() {
  const [params, setParams] = useSearchParams();
  const run = params.get("run") ?? "";
  const q = useQuery({ queryKey: ["cs-archive", "runs", run], queryFn: () => api<{ ready: boolean; runs: Run[]; run_id: string | null; issues: Issue[] }>(`/cs-archive/runs${run ? `?run=${run}` : ""}`) });
  if (q.data && !q.data.ready) return <CsNotReady />;
  const current = q.data?.runs.find((r) => r.id === q.data?.run_id);

  return (
    <div className="mx-auto w-full max-w-[1400px] px-1 pb-16">
      <Crumbs> / Import log</Crumbs>
      <h1 className="mt-5 text-[36px] font-semibold tracking-[-1px] text-ink">Import log</h1>
      <p className="mt-2 text-[16px] text-muted">What each import brought in from commonsku, and anything that's missing or needs a look.</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-[360px_1fr]">
        <ul className="space-y-2">
          {(q.data?.runs ?? []).map((r) => (
            <li key={r.id}>
              <button type="button" onClick={() => setParams(new URLSearchParams({ run: r.id }))} className={`w-full rounded-md border px-4 py-3 text-left ${r.id === q.data?.run_id ? "border-ink" : "border-rule"} hover:bg-canvas`}>
                <p className="text-[15px] font-semibold text-ink">{csDay(r.started_at)} · {r.status}</p>
                <p className="mt-0.5 line-clamp-2 text-[13px] text-muted">{r.label}</p>
                <p className="mt-1 text-[13px] text-muted">{Object.entries(r.issue_counts).map(([k, n]) => `${n} ${KIND[k]?.toLowerCase() ?? k}`).join(" · ") || "no issues"}</p>
              </button>
            </li>
          ))}
          {q.data && !q.data.runs.length ? <p className="text-muted">Nothing has been imported yet.</p> : null}
        </ul>
        <section>
          {current?.summary?.clients ? (
            <table className="w-full text-left text-[14px]">
              <thead className="border-b border-rule text-muted"><tr><th className="py-2 font-medium">Client</th><th className="py-2 text-right font-medium">Projects</th><th className="py-2 text-right font-medium">Documents</th><th className="py-2 text-right font-medium">Originals</th><th className="py-2 text-right font-medium">Files</th></tr></thead>
              <tbody>
                {current.summary.clients.map((c, i) => (
                  <tr key={i} className="border-b border-rule"><td className="py-2 text-ink">{String(c.name)}</td><td className="py-2 text-right">{c.projects}/{c.projectsListed}</td><td className="py-2 text-right">{c.documents}</td><td className="py-2 text-right">{c.originals}</td><td className="py-2 text-right">{c.files}</td></tr>
                ))}
              </tbody>
            </table>
          ) : null}
          <h2 className="mt-8 text-[18px] font-semibold text-ink">Needs a look ({q.data?.issues.length ?? 0})</h2>
          <ul className="mt-2">
            {(q.data?.issues ?? []).map((i) => (
              <li key={i.id} className="border-b border-rule py-3 text-[14px]"><span className="mr-2 rounded bg-canvas px-2 py-0.5 text-[12px] font-semibold text-ink">{KIND[i.kind] ?? i.kind}</span><span className="text-ink">{i.detail}</span></li>
            ))}
          </ul>
          {q.data && !q.data.issues.length ? <p className="mt-2 text-muted">Nothing missing or failed in this run.</p> : null}
        </section>
      </div>
    </div>
  );
}
